import { useMemo, useState } from "react";
import { FileX2, Gauge, HandHeart, Hourglass, TimerReset, TrendingDown, UserMinus } from "lucide-react";

import { usePageBack } from "../../context/PageBackContext.js";
import { cn } from "../../lib/utils.js";
import { reportAnchor, reportRange, shiftReportAnchor, REPORT_PERIOD_OPTIONS, type ReportPeriod } from "../../lib/reportRange.js";
import { SummaryStatSuffix } from "../../components/SummaryStatCard.js";
import { DropdownMultiSelect, DropdownSelect } from "../../components/DropdownButton.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { SpecularButton } from "../../components/ui/specular-button.js";
import { Badge, type BadgeProps } from "../../components/ui/badge.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import { ChartPanel } from "../../components/ui/charts/ChartPanel.js";
import { TrendArea } from "../../components/ui/charts/TrendArea.js";
import type { PaginationMeta } from "../../components/ui/pagination.js";
import {
  ReportPage, FilterBar, ErrorBanner, OrgFilters, ExportButton, CardGrid, ReportStatCard, ReportTableFrame, ReportFooter, PageSizeSelect,
  TableScrollStart, TableToolbar, YesNoBadge, dropdownProps, splitList, joinList, stickyName, stickyCode, NAME_WIDTH, CODE_WIDTH, type OpenState,
} from "../../components/reports/ReportKit.js";
import {
  useUrlFilters, useDebouncedSearch, useReport, useOrgMeta, toParams, downloadReport,
} from "../../components/reports/useReport.js";

type ExitType = "voluntary" | "involuntary" | "other";
interface ExitRow {
  id: string; name: string; code: string; department: string | null; office: string | null;
  hireDate: string | null; resignationDate: string | null; lastWorkingDay: string | null;
  tenureMonths: number; reason: string; reasonLabel: string; exitType: ExitType; earlyExit: boolean;
  exitInterviewDone: boolean; offboardingStatus: string;
}
interface GroupFigures {
  opening: number; closing: number; avgHeadcount: number; exits: number;
  voluntary: number; involuntary: number; other: number; attritionPct: number; annualisedPct: number;
}
interface AttritionReport {
  from: string; to: string;
  rows: ExitRow[];
  /** Loaded by a separate request (`part=cards`), so a page turn never refetches it. */
  cards?: {
    exits: number; opening: number; closing: number; attritionPct: number; annualisedPct: number;
    voluntary: number; involuntary: number; voluntaryPct: number;
    avgTenureMonths: number | null; earlyExits: number; withoutRecord: number; months: number;
    groupBy: string; groupTitle: string;
    groups: (GroupFigures & { key: string; label: string })[];
    totals: GroupFigures;
    trend: { month: string; exits: number; attritionPct: number; headcount: number }[];
  };
  meta: PaginationMeta;
}

const DEFAULTS = {
  search: "", officeIds: "", departmentIds: "", employeeTypes: "", exitTypes: "", groupBy: "department",
  period: "year", anchor: "", page: "1", limit: "25",
};
// The cards, group table and trend cover everyone in scope; search and exit type narrow only the exit list.
const ROW_ONLY = ["search", "exitTypes"];
const API = "/reports/hr/attrition";

const GROUP_OPTIONS = [
  { value: "department", label: "Group by Department" },
  { value: "office", label: "Group by Office" },
  { value: "employeeType", label: "Group by Employee type" },
];
const TYPE_OPTIONS = [
  { value: "FULL_TIME", label: "Full-time" },
  { value: "PART_TIME", label: "Part-time" },
  { value: "CONTRACTOR", label: "Contractor" },
  { value: "INTERN", label: "Intern" },
];
const EXIT_TYPE_OPTIONS = [
  { value: "voluntary", label: "Voluntary" },
  { value: "involuntary", label: "Involuntary" },
  { value: "other", label: "Other (contract end)" },
];
const EXIT_BADGE: Record<ExitType, { tone: BadgeProps["tone"]; label: string }> = {
  voluntary: { tone: "info", label: "Voluntary" },
  involuntary: { tone: "error", label: "Involuntary" },
  other: { tone: "neutral", label: "Other" },
};

const TREND_MONTH = new Intl.DateTimeFormat("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });
const NUM = "px-3 text-center tabular-nums";

/** A group's figures, in the column order of the table header. */
function FigureCells({ r }: { r: GroupFigures }) {
  return (
    <>
      <TableCell className={NUM}>{r.opening}</TableCell>
      <TableCell className={NUM}>{r.closing}</TableCell>
      <TableCell className={NUM}>{r.avgHeadcount}</TableCell>
      <TableCell className={cn(NUM, "font-bold", r.exits > 0 && "text-[var(--color-error-fg)]")}>{r.exits}</TableCell>
      <TableCell className={NUM}>{r.voluntary}</TableCell>
      <TableCell className={NUM}>{r.involuntary}</TableCell>
      <TableCell className={NUM}>{r.other}</TableCell>
      <TableCell className={cn(NUM, "border-l border-surface-200 font-semibold", r.attritionPct > 0 && "text-[var(--color-warning-fg)]")}>{r.attritionPct}%</TableCell>
      <TableCell className={cn(NUM, "font-semibold")}>{r.annualisedPct}%</TableCell>
    </>
  );
}

export default function AttritionReportPage() {
  usePageBack("/dashboard");
  const [f, setF] = useUrlFilters(DEFAULTS);
  const [search, setSearch] = useDebouncedSearch(f.search, (v) => setF({ search: v }));
  const [open, setOpen] = useState<string | null>(null);
  const openState: OpenState = { open, setOpen };
  const org = useOrgMeta();

  const period = (REPORT_PERIOD_OPTIONS.some((o) => o.value === f.period) ? f.period : "year") as ReportPeriod;
  const anchor = f.anchor || reportAnchor(period);
  const range = reportRange(period, anchor);

  const params = useMemo(() => {
    const { period: _p, anchor: _a, ...rest } = f;
    return toParams(rest, { from: range.from, to: range.to });
  }, [f, range.from, range.to]);
  const { data, cards: c, loading, error } = useReport<AttritionReport>(API, params, "AttritionReportPage", ROW_ONLY);

  const rows = data?.rows ?? [];
  const trends = useMemo(() => {
    const label = (m: string) => TREND_MONTH.format(new Date(`${m}-01T00:00:00Z`));
    return {
      exits: (c?.trend ?? []).map((t) => ({ label: label(t.month), value: t.exits })),
      pct: (c?.trend ?? []).map((t) => ({ label: label(t.month), value: t.attritionPct })),
    };
  }, [c?.trend]);

  return (
    <ReportPage
      title="Attrition Reports"
      description="Exits against average headcount, voluntary and involuntary, with who left and when."
    >
      <CardGrid>
        <ReportStatCard icon={UserMinus} tone="danger" label="Exits" value={c?.exits ?? "—"} caption={range.label} />
        <ReportStatCard
          icon={TrendingDown} tone="warning" label="Attrition"
          value={<>{c?.attritionPct ?? "—"}<SummaryStatSuffix> %</SummaryStatSuffix></>}
          caption={c ? `Over average headcount of ${c.totals.avgHeadcount}` : undefined}
        />
        <ReportStatCard
          icon={Gauge} tone="rose" label="Annualised"
          value={<>{c?.annualisedPct ?? "—"}<SummaryStatSuffix> %</SummaryStatSuffix></>}
          caption={c ? `Scaled from ${c.months} month${c.months === 1 ? "" : "s"} to 12` : undefined}
        />
        <ReportStatCard
          icon={HandHeart} tone="info" label="Voluntary"
          value={<>{c?.voluntaryPct ?? "—"}<SummaryStatSuffix> %</SummaryStatSuffix></>}
          caption={c ? `${c.voluntary} voluntary, ${c.involuntary} involuntary` : undefined}
        />
        <ReportStatCard
          icon={Hourglass} tone="accent" label="Avg Tenure at Exit"
          value={c ? (c.avgTenureMonths == null ? "—" : <>{c.avgTenureMonths}<SummaryStatSuffix> months</SummaryStatSuffix></>) : "—"}
          caption="Hire date to last working day"
        />
        <ReportStatCard icon={TimerReset} tone="cyan" label="Early Attrition" value={c?.earlyExits ?? "—"} caption="Left within 6 months of joining" />
        <ReportStatCard
          icon={FileX2} tone="brand" label="No Offboarding Record" value={c?.withoutRecord ?? "—"}
          caption="Terminated without an exit date, so not counted"
        />
      </CardGrid>

      <div className="grid gap-3 md:grid-cols-2">
        <ChartPanel title="Exits per month" hint="Last 12 months, by last working day">
          <TrendArea data={trends.exits} formatValue={(n) => `${n} exits`} formatTick={(n) => String(n)} height={180} />
        </ChartPanel>
        <ChartPanel title="Attrition % per month" hint="Each month's exits over its average headcount">
          <TrendArea data={trends.pct} formatValue={(n) => `${n}%`} formatTick={(n) => `${n}%`} height={180} />
        </ChartPanel>
      </div>

      <FilterBar>
        <SearchInput className="w-56" placeholder="Search name or code" value={search} onChange={(e) => setSearch(e.target.value)} />
        <OrgFilters meta={org} officeIds={f.officeIds} departmentIds={f.departmentIds} onChange={setF} openState={openState} />
        <DropdownMultiSelect
          values={splitList(f.employeeTypes)} onChange={(v) => setF({ employeeTypes: joinList(v) })} options={TYPE_OPTIONS}
          allLabel="All employee types" ariaLabel="Employee type" {...dropdownProps("type", openState)}
        />
        <DropdownMultiSelect
          values={splitList(f.exitTypes)} onChange={(v) => setF({ exitTypes: joinList(v) })} options={EXIT_TYPE_OPTIONS}
          allLabel="All exit types" ariaLabel="Exit type" {...dropdownProps("exitType", openState)}
        />
        <DropdownSelect
          value={f.groupBy} onChange={(v) => setF({ groupBy: v })} options={GROUP_OPTIONS}
          placeholder="Group by" ariaLabel="Group by" clearable={false} {...dropdownProps("group", openState)}
        />
        <DropdownSelect
          value={period} onChange={(v) => setF({ period: v, anchor: "" })} options={REPORT_PERIOD_OPTIONS}
          placeholder="Period" ariaLabel="Period" clearable={false} {...dropdownProps("period", openState)}
        />
        <div className="flex items-center gap-3">
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor(period, anchor, -1) })} aria-label="Previous period" className="w-8 px-0">&larr;</SpecularButton>
          <div className="min-w-[150px] text-center text-sm font-semibold">{range.label}</div>
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor(period, anchor, 1) })} aria-label="Next period" className="w-8 px-0">&rarr;</SpecularButton>
        </div>
      </FilterBar>

      <TableToolbar left={<span className="text-[12px] text-surface-500">Groups use each employee&apos;s current department, office and details, not where they were on each date.</span>}>
        <PageSizeSelect limit={f.limit} onLimit={(v) => setF({ limit: v })} openState={openState} />
        <ExportButton onExport={() => downloadReport(API, params, `attrition-${range.from}-to-${range.to}.xlsx`)} />
      </TableToolbar>

      <ErrorBanner message={error} />

      <div className="flex flex-col gap-2">
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.5px] text-surface-700">Summary by {c?.groupTitle?.toLowerCase() ?? "group"}</h2>
        <ReportTableFrame loading={!c && !error} empty={!c || c.groups.length === 0} emptyText={`No employees match these filters for ${range.label}.`} contentKey={c?.groupBy ?? ""}>
          <Table className="w-max min-w-full">
            <TableHeader>
              <TableRow className="bg-surface-50">
                <TableHead className="px-3">{c?.groupTitle ?? "Group"}</TableHead>
                <TableHead className="px-3 text-center">Opening</TableHead>
                <TableHead className="px-3 text-center">Closing</TableHead>
                <TableHead className="px-3 text-center">Avg headcount</TableHead>
                <TableHead className="px-3 text-center">Exits</TableHead>
                <TableHead className="px-3 text-center">Voluntary</TableHead>
                <TableHead className="px-3 text-center">Involuntary</TableHead>
                <TableHead className="px-3 text-center" title="Contract end and any other reason">Other</TableHead>
                <TableHead className="border-l border-surface-200 px-3 text-center">Attrition %</TableHead>
                <TableHead className="px-3 text-center" title="The period's attrition scaled to 12 months">Annualised %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(c?.groups ?? []).map((g) => (
                <TableRow key={g.key || "none"} className="hover:bg-surface-50">
                  <TableCell className="px-3 font-semibold text-surface-900">{g.label}</TableCell>
                  <FigureCells r={g} />
                </TableRow>
              ))}
              {c && (
                <TableRow className="bg-surface-50 font-bold hover:bg-surface-50">
                  <TableCell className="px-3 text-surface-900">Total</TableCell>
                  <FigureCells r={c.totals} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ReportTableFrame>
      </div>

      <h2 className="text-[13px] font-semibold uppercase tracking-[0.5px] text-surface-700">Who left</h2>

      <ReportTableFrame loading={loading} empty={rows.length === 0} emptyText={`No exits match these filters for ${range.label}.`}>
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow className="bg-surface-50">
              <TableHead className={cn(stickyName, "bg-surface-50")}><div className={NAME_WIDTH}><TableScrollStart />Employee Name</div></TableHead>
              <TableHead className={cn(stickyCode, "bg-surface-50")}><div className={CODE_WIDTH}>Emp Code</div></TableHead>
              <TableHead className="px-3">Department</TableHead>
              <TableHead className="px-3">Hire date</TableHead>
              <TableHead className="px-3">Resigned on</TableHead>
              <TableHead className="px-3">Last working day</TableHead>
              <TableHead className="px-3 text-center" title="Whole months from hire date to last working day">Tenure (months)</TableHead>
              <TableHead className="px-3">Reason</TableHead>
              <TableHead className="px-3 text-center">Type</TableHead>
              <TableHead className="px-3 text-center" title="Left within 6 months of joining">Early exit</TableHead>
              <TableHead className="px-3 text-center">Exit interview</TableHead>
              <TableHead className="px-3 text-center">Offboarding</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="group hover:bg-surface-50">
                <TableCell className={cn(stickyName, "bg-surface-0 group-hover:bg-surface-50")}>
                  <div className={cn(NAME_WIDTH, "truncate font-semibold text-surface-900")} title={[r.name, r.department, r.office].filter(Boolean).join(" · ")}>{r.name}</div>
                </TableCell>
                <TableCell className={cn(stickyCode, "bg-surface-0 text-surface-600 group-hover:bg-surface-50")}>
                  <div className={cn(CODE_WIDTH, "truncate")}>{r.code}</div>
                </TableCell>
                <TableCell className="px-3">{r.department ?? "—"}</TableCell>
                <TableCell className="px-3 whitespace-nowrap">{r.hireDate ?? "—"}</TableCell>
                <TableCell className="px-3 whitespace-nowrap">{r.resignationDate ?? "—"}</TableCell>
                <TableCell className="px-3 whitespace-nowrap font-semibold">{r.lastWorkingDay ?? "—"}</TableCell>
                <TableCell className={cn(NUM, r.earlyExit && "font-semibold text-[var(--color-warning-fg)]")}>{r.tenureMonths}</TableCell>
                <TableCell className="px-3 whitespace-nowrap">{r.reasonLabel}</TableCell>
                <TableCell className="px-3 text-center"><Badge tone={EXIT_BADGE[r.exitType].tone}>{EXIT_BADGE[r.exitType].label}</Badge></TableCell>
                <TableCell className="px-3 text-center">{r.earlyExit ? <Badge tone="warning">Early</Badge> : <span className="text-surface-300">—</span>}</TableCell>
                <TableCell className="px-3 text-center"><YesNoBadge state={r.exitInterviewDone ? "Y" : "N"} /></TableCell>
                <TableCell className="px-3 text-center">
                  <Badge tone={r.offboardingStatus === "COMPLETED" ? "success" : "warning"}>{r.offboardingStatus === "COMPLETED" ? "Completed" : "In progress"}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ReportTableFrame>

      <ReportFooter meta={data?.meta} onPage={(p) => setF({ page: String(p) })} />
    </ReportPage>
  );
}
